/**
 * Application Theme Configuration
 * Centralized theme system for easy customization
 *
 * To change the theme, update the color values in this file.
 * All components reference these values for consistency.
 */

import { createTheme } from '@mui/material/styles';

// ==================== COLOR PALETTE ====================

/**
 * Primary Colors
 * Used for main branding, primary actions, and key UI elements
 */
const primaryColors = {
  main: '#1976d2',       // Primary blue
  light: '#42a5f5',      // Lighter variant
  dark: '#1565c0',       // Darker variant
  contrastText: '#ffffff', // Text color on primary background
};

/**
 * Secondary Colors
 * Used for accents, secondary actions, and supporting elements
 */
const secondaryColors = {
  main: '#dc004e',       // Secondary pink/red
  light: '#ff5983',      // Lighter variant
  dark: '#9a0036',       // Darker variant
  contrastText: '#ffffff', // Text color on secondary background
};

/**
 * Error Colors
 * Used for error states, warnings, and critical alerts
 */
const errorColors = {
  main: '#f44336',       // Error red
  light: '#e57373',      // Lighter variant
  dark: '#d32f2f',       // Darker variant
  contrastText: '#ffffff',
};

/**
 * Warning Colors
 * Used for warning states and cautions
 */
const warningColors = {
  main: '#ff9800',       // Warning orange
  light: '#ffb74d',      // Lighter variant
  dark: '#f57c00',       // Darker variant
  contrastText: '#000000',
};

/**
 * Info Colors
 * Used for informational messages
 */
const infoColors = {
  main: '#2196f3',       // Info blue
  light: '#64b5f6',      // Lighter variant
  dark: '#1976d2',       // Darker variant
  contrastText: '#ffffff',
};

/**
 * Success Colors
 * Used for success states and positive feedback
 */
const successColors = {
  main: '#4caf50',       // Success green
  light: '#81c784',      // Lighter variant
  dark: '#388e3c',       // Darker variant
  contrastText: '#ffffff',
};

/**
 * Neutral/Gray Colors
 * Used for text, backgrounds, borders, and dividers
 */
const grayColors = {
  50: '#fafafa',
  100: '#f5f5f5',
  200: '#eeeeee',
  300: '#e0e0e0',
  400: '#bdbdbd',
  500: '#9e9e9e',
  600: '#757575',
  700: '#616161',
  800: '#424242',
  900: '#212121',
};

/**
 * Background Colors
 * Used for page backgrounds, cards, and surfaces
 */
const backgroundColors = {
  default: '#fafafa',    // Main background
  paper: '#ffffff',      // Card/paper background
  sidebar: '#1e293b',    // Sidebar background
  header: '#0f172a',     // AppBar/header background
};

/**
 * Text Colors
 * Used for all text content
 */
const textColors = {
  primary: 'rgba(0, 0, 0, 0.87)',    // Primary text
  secondary: 'rgba(0, 0, 0, 0.6)',   // Secondary text
  disabled: 'rgba(0, 0, 0, 0.38)',   // Disabled text
  hint: 'rgba(0, 0, 0, 0.38)',       // Hint text
};

// ==================== STATUS COLORS ====================

/**
 * Contract Status Colors
 * Used for contract status badges and indicators
 */
export const contractStatusColors = {
  'Draft': '#9e9e9e',                // Gray
  'Pending Approval': '#ff9800',     // Orange
  'Approved': '#2196f3',             // Blue
  'Active': '#4caf50',               // Green
  'Matured': '#9c27b0',              // Purple
  'Paid Out': '#00bcd4',             // Cyan
  'Early Terminated': '#f44336',     // Red
  'Cancelled': '#607d8b',            // Blue Gray
};

/**
 * Payment Status Colors
 * Used for payment status badges and indicators
 */
export const paymentStatusColors = {
  'pending': '#ff9800',              // Orange
  'paid': '#4caf50',                 // Green
  'partial': '#2196f3',              // Blue
  'overpaid': '#9c27b0',             // Purple
  'overdue': '#f44336',              // Red
  'matched': '#4caf50',              // Green
  'unmatched': '#9e9e9e',            // Gray
};

// ==================== TYPOGRAPHY ====================

/**
 * Font Families
 */
const fontFamilies = {
  primary: '"Roboto", "Helvetica", "Arial", sans-serif',
  secondary: '"Roboto", "Helvetica", "Arial", sans-serif',
  monospace: '"Roboto Mono", "Courier New", monospace',
};

/**
 * Font Sizes
 */
const fontSizes = {
  xs: '0.75rem',    // 12px
  sm: '0.875rem',   // 14px
  base: '1rem',     // 16px
  lg: '1.125rem',   // 18px
  xl: '1.25rem',    // 20px
  '2xl': '1.5rem',  // 24px
  '3xl': '1.875rem',// 30px
  '4xl': '2.25rem', // 36px
};

/**
 * Font Weights
 */
const fontWeights = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

// ==================== SPACING ====================

/**
 * Spacing Scale
 * Used for margins, padding, and gaps
 */
export const spacing = {
  xs: 4,    // 4px
  sm: 8,    // 8px
  md: 16,   // 16px
  lg: 24,   // 24px
  xl: 32,   // 32px
  '2xl': 48,  // 48px
  '3xl': 64,  // 64px
};

// ==================== BORDERS ====================

/**
 * Border Radius
 */
export const borderRadius = {
  none: 0,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
  full: 9999,
};

/**
 * Border Widths
 */
export const borderWidth = {
  thin: 1,
  medium: 2,
  thick: 4,
};

// ==================== SHADOWS ====================

/**
 * Box Shadows
 * Used for elevation and depth
 */
export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
};

// ==================== Z-INDEX ====================

/**
 * Z-Index Scale
 * Used for layering and stacking
 */
export const zIndex = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
};

// ==================== BREAKPOINTS ====================

/**
 * Responsive Breakpoints
 * Used for media queries and responsive design
 */
export const breakpoints = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

// ==================== MATERIAL-UI THEME ====================

/**
 * Create Material-UI Theme
 * This is the main theme object used throughout the application
 */
const theme = createTheme({
  palette: {
    primary: primaryColors,
    secondary: secondaryColors,
    error: errorColors,
    warning: warningColors,
    info: infoColors,
    success: successColors,
    background: backgroundColors,
    text: textColors,
    grey: grayColors,
  },
  typography: {
    fontFamily: fontFamilies.primary,
    fontSize: 14,
    fontWeightLight: fontWeights.light,
    fontWeightRegular: fontWeights.regular,
    fontWeightMedium: fontWeights.medium,
    fontWeightBold: fontWeights.bold,
    h1: {
      fontSize: fontSizes['4xl'],
      fontWeight: fontWeights.bold,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: fontSizes['3xl'],
      fontWeight: fontWeights.bold,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: fontSizes['2xl'],
      fontWeight: fontWeights.semibold,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: fontSizes.xl,
      fontWeight: fontWeights.semibold,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: fontSizes.lg,
      fontWeight: fontWeights.medium,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: fontSizes.base,
      fontWeight: fontWeights.medium,
      lineHeight: 1.6,
    },
    subtitle1: {
      fontSize: fontSizes.base,
      fontWeight: fontWeights.regular,
      lineHeight: 1.75,
    },
    subtitle2: {
      fontSize: fontSizes.sm,
      fontWeight: fontWeights.medium,
      lineHeight: 1.57,
    },
    body1: {
      fontSize: fontSizes.base,
      fontWeight: fontWeights.regular,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: fontSizes.sm,
      fontWeight: fontWeights.regular,
      lineHeight: 1.43,
    },
    button: {
      fontSize: fontSizes.sm,
      fontWeight: fontWeights.medium,
      textTransform: 'none', // Disable uppercase transformation
      lineHeight: 1.75,
    },
    caption: {
      fontSize: fontSizes.xs,
      fontWeight: fontWeights.regular,
      lineHeight: 1.66,
    },
    overline: {
      fontSize: fontSizes.xs,
      fontWeight: fontWeights.regular,
      textTransform: 'uppercase',
      lineHeight: 2.66,
    },
  },
  spacing: 8, // Base spacing unit (8px)
  shape: {
    borderRadius: borderRadius.md,
  },
  breakpoints: {
    values: breakpoints,
  },
  components: {
    // Button customization
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
          textTransform: 'none',
          fontWeight: fontWeights.medium,
        },
        contained: {
          boxShadow: shadows.sm,
          '&:hover': {
            boxShadow: shadows.md,
          },
        },
      },
    },
    // Card customization
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.lg,
          boxShadow: shadows.sm,
        },
      },
    },
    // Paper customization
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
        },
        elevation1: {
          boxShadow: shadows.sm,
        },
        elevation2: {
          boxShadow: shadows.md,
        },
        elevation3: {
          boxShadow: shadows.lg,
        },
      },
    },
    // Table customization
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${grayColors[200]}`,
        },
        head: {
          fontWeight: fontWeights.semibold,
          backgroundColor: grayColors[50],
        },
      },
    },
    // TextField customization
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    // Chip customization
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.sm,
        },
      },
    },
  },
});

export default theme;

// ==================== CUSTOM THEME UTILITIES ====================

/**
 * Get status color
 * @param {string} status - Status value
 * @param {string} type - Status type ('contract' or 'payment')
 * @returns {string} Color hex code
 */
export const getStatusColor = (status, type = 'contract') => {
  const colors = type === 'contract' ? contractStatusColors : paymentStatusColors;
  return colors[status] || grayColors[500];
};

/**
 * Get contrasting text color for a background color
 * @param {string} backgroundColor - Background color hex
 * @returns {string} 'light' or 'dark'
 */
export const getContrastText = (backgroundColor) => {
  // Simple luminance calculation
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? 'dark' : 'light';
};

// ==================== THEME PRESETS ====================

/**
 * Alternative Theme Presets
 * Uncomment and use these to quickly switch themes
 */

// Select Africa Theme (from Styling Guide)
export const selectAfricaTheme = createTheme({
  ...theme,
  palette: {
    ...theme.palette,
    primary: {
      main: '#DD052B',        // Pantone 186C (Red) - R221 G5 B43
      light: '#E5476A',
      dark: '#B00422',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#00967F',        // Pantone 334C (Turquoise) - R0 G150 B127
      light: '#33AE9B',
      dark: '#006F5F',
      contrastText: '#ffffff',
    },
    info: {
      main: '#8FD1E3',        // Pantone 639C (Light Blue) - R143 G209 B227
      light: '#AFDDE9',
      dark: '#6FBDD5',
      contrastText: '#000000',
    },
    warning: {
      main: '#BF883C',        // Pantone 7510C (Gold) - R191 G136 B60
      light: '#CFA366',
      dark: '#996C2F',
      contrastText: '#ffffff',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
      sidebar: '#1D292F',     // Pantone 7547C (Dark Navy) - R29 G41 B47
      header: '#ffffff',
    },
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#eeeeee',
      300: '#e0e0e0',
      400: '#bdbdbd',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#1D292F',         // Pantone 7547C (Dark Navy)
      900: '#231F20',         // Process Black - R35 G31 B32
    },
  },
});

// African Alliance Green Theme
export const africanAllianceTheme = createTheme({
  ...theme,
  palette: {
    ...theme.palette,
    primary: {
            main: '#003d5c',        // African Alliance Navy
      light: '#336180',
      dark: '#002940',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#003d5c',        // African Alliance Navy
      light: '#336180',
      dark: '#002940',
      contrastText: '#382929ff',
    },
  },
});

// Dark Mode Theme
export const darkTheme = createTheme({
  ...theme,
  palette: {
    mode: 'dark',
    primary: primaryColors,
    secondary: secondaryColors,
    background: {
      default: '#121212',
      paper: '#1e1e1e',
      sidebar: '#000000',
      header: '#1e1e1e',
    },
    text: {
      primary: 'rgba(255, 255, 255, 0.87)',
      secondary: 'rgba(255, 255, 255, 0.6)',
      disabled: 'rgba(255, 255, 255, 0.38)',
      hint: 'rgba(255, 255, 255, 0.38)',
    },
  },
});

/**
 * USAGE INSTRUCTIONS:
 *
 * 1. To change the application theme, update the color values at the top of this file
 * 2. To use a preset theme, change the import in App.js:
 *    import theme from './theme/theme';                        // Default theme
 *    import { selectAfricaTheme as theme } from './theme/theme'; // Select Africa theme
 *    import { africanAllianceTheme as theme } from './theme/theme'; // African Alliance theme
 *    import { darkTheme as theme } from './theme/theme';         // Dark theme
 * 3. To create a custom theme, copy the theme object and modify values
 * 4. All components should reference theme colors via:
 *    - theme.palette.primary.main
 *    - theme.palette.background.paper
 *    - etc.
 * 5. For status colors, use the exported helper functions:
 *    - getStatusColor(status, 'contract')
 *    - getStatusColor(status, 'payment')
 *
 * AVAILABLE THEMES:
 * - theme (default)           - Standard Material-UI blue theme
 * - selectAfricaTheme         - Select Africa branding (Red #E22736, Turquoise #1FB9B7)
 * - africanAllianceTheme      - African Alliance branding (Green #00a651, Navy #003d5c)
 * - darkTheme                 - Dark mode variant
 */
